import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Box, TextField } from '@mui/material';
import { CreateSupplierDto } from '@shared/dto/supplier/create-supplier.dto';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../components/dialogs/form-dialog';
import { SimilarSupplierDialog } from '../../../components/dialogs/similar-supplier-dialog';
import { PhoneInput } from '../../../components/phone-input';
import { useSupplierVatHint } from '../../../hooks/use-supplier-vat-hint';
import type { ExtractedSupplierFormData } from '../../../services/goods-receiving/extraction-terminal-action.service';
import {
  getSimilarSuppliersConflict,
  type SimilarSupplierCandidate,
} from '../../../services/suppliers/similar-suppliers';
import { toCreateSupplierDto } from '../../suppliers/mappers/supplier.mapper';
import { useCreateSupplierFromGoodsReceiving } from '../hooks/use-delivery-note-mutations';

interface SupplierFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (supplier: { id: string; name: string }) => void;
  /** User picked an existing supplier from the similar-suppliers conflict dialog instead of creating a new one. Falls back to onCreated when absent. */
  onUseExisting?: (supplier: { id: string; name: string; vatNumber: string }) => void;
  initialData?: ExtractedSupplierFormData;
}

interface SimilarSuppliersConflictState {
  candidates: SimilarSupplierCandidate[];
  dto: CreateSupplierDto;
}

export function SupplierFormDialog({
  open,
  onClose,
  onCreated,
  onUseExisting,
  initialData,
}: SupplierFormDialogProps) {
  const { t } = useTranslation('goodsReceiving');
  const createMutation = useCreateSupplierFromGoodsReceiving();
  const [conflict, setConflict] = useState<SimilarSuppliersConflictState | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateSupplierDto>({
    resolver: classValidatorResolver(CreateSupplierDto),
    defaultValues: {
      name: '',
      vatNumber: '',
      address: '',
      phone: '',
      email: '',
    },
    mode: 'onChange',
  });
  const vatNumber = useWatch({ control, name: 'vatNumber' });
  const { messageKey: vatHintKey, isWarning: isVatToReview } = useSupplierVatHint({
    vatNumber,
    hasFormatError: !!errors.vatNumber,
  });

  useEffect(
    function resetOnOpen() {
      if (open) {
        reset({
          name: initialData?.name ?? '',
          vatNumber: initialData?.vatNumber ?? '',
          address: initialData?.address ?? '',
          phone: initialData?.phone ?? '',
          email: initialData?.email ?? '',
        });
      }
    },
    [open, initialData, reset],
  );

  const onSubmit = async (data: CreateSupplierDto) => {
    const dto = toCreateSupplierDto(data);
    try {
      const created = await createMutation.mutateAsync(dto);
      onCreated(created);
      onClose();
    } catch (err) {
      const candidates = getSimilarSuppliersConflict(err);
      if (candidates) {
        setConflict({ candidates, dto });
        return;
      }
      // Altri errori: già mostrati dal toast generico nell'onError dell'hook.
    }
  };

  async function handleCreateAnyway() {
    if (!conflict) {
      return;
    }
    try {
      const created = await createMutation.mutateAsync({
        ...conflict.dto,
        confirmDuplicate: true,
      });
      setConflict(null);
      onCreated(created);
      onClose();
    } catch {
      // Altri errori: già mostrati dal toast generico nell'onError dell'hook.
    }
  }

  function handleUseExisting(candidate: SimilarSupplierCandidate) {
    setConflict(null);
    onClose();
    if (onUseExisting) {
      onUseExisting(candidate);
    } else {
      onCreated(candidate);
    }
  }

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        title={t('newSupplierTitle')}
        onSubmit={handleSubmit(onSubmit)}
        isSubmitting={createMutation.isPending}
        isValid={isValid}
        onTransitionExited={() => reset()}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <TextField
            label={t('supplierName')}
            {...register('name')}
            autoComplete="off"
            fullWidth
            autoFocus
            required
            error={!!errors.name}
            helperText={errors.name?.message}
          />
          <TextField
            label={t('vatNumber')}
            {...register('vatNumber')}
            autoComplete="off"
            fullWidth
            required
            error={!!errors.vatNumber}
            color={isVatToReview ? 'warning' : undefined}
            helperText={t(vatHintKey)}
            slotProps={{
              htmlInput: { maxLength: 11, inputMode: 'numeric' },
              formHelperText: isVatToReview
                ? { role: 'status', sx: { color: 'warning.dark' } }
                : undefined,
            }}
          />
        </Box>

        <TextField
          label={t('address')}
          {...register('address')}
          autoComplete="off"
          fullWidth
          multiline
          rows={2}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <PhoneInput
                label={t('phone')}
                value={field.value || ''}
                onChange={field.onChange}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <TextField
            label={t('email')}
            {...register('email')}
            autoComplete="off"
            fullWidth
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </Box>
      </FormDialog>
      <SimilarSupplierDialog
        open={!!conflict}
        attemptedName={conflict?.dto.name ?? ''}
        candidates={conflict?.candidates ?? []}
        onClose={() => setConflict(null)}
        onUseExisting={handleUseExisting}
        onCreateAnyway={handleCreateAnyway}
        isSubmitting={createMutation.isPending}
      />
    </>
  );
}
